import axios from "axios";
import config from "config";
import { isNil } from "lodash";
import ReactGA from "react-ga";
import { isNativePlatform } from "./capacitor/appsHelpers";
import isPes, { isLek } from "./helpers/isPes";
import { getUserInfo } from "./hooks/useUser";

const { CF_AUTH_CLIENT_ID, CF_AUTH_CLIENT_SECRET, NEW_API_URL, serviceId } =
	config;

export const defaultAppConfig = {
	maxQuestions: 2000,
	maxImageSizeMb: 5,
	maxCompressionSizeMb: 3,
	aiChatbotEnabled: false,
	copyContentSettings: {
		comments: {
			allowCopy: false,
			sendOnCopy: false,
		},
		question: {
			allowCopy: false,
			sendOnCopy: false,
		},
		discussion: {
			allowCopy: false,
			sendOnCopy: false,
		},
		articles: {
			allowCopy: false,
			sendOnCopy: false,
		},
	},
	statisticsDateRangeInDays: 30,
	decks: {
		maxDecksInBatch: 50,
	},
	faq: false,
};

export function buildQuery(filters) {
	const selectedTags = (filters.tags || []).filter(
		(tag) => !(filters.departments || []).some((d) => d.index === tag.dzialId),
	);
	const selectedSubtags = (filters.subtags || []).filter(
		(st) => !(filters.tags || []).some((t) => t.index === st.tagId),
	);
	const { skipCanceledQuestions, isUnanswered } =
		filters.additionalOption || {};
	const cemVersion =
		filters.typeOfExam === "cem" && filters.cemVersions.length === 1
			? filters.cemVersions
			: null;

	return Object.entries({
		termsIds: (filters.terms || []).map(({ id }) => id),
		consultantsIds: (filters.consultants || []).map(({ id }) => id),
		finishedExamsIds: (filters.finishedExamsIds || []).map(({ id }) => id),
		unfinishedExamsIds: (filters.unfinishedExamsIds || []).map(({ id }) => id),
		examsIds: (filters.examsIds || []).map(({ id }) => id),
		categoriesIds: (filters.departments || []).map(({ id }) => id),
		ids: (filters.questions || []).map((id) => id),
		tagsIds: selectedTags.map(({ id }) => id),
		subTagsIds: selectedSubtags.map(({ id }) => id),
		level: (filters.level || []).map((level) => level),
		...getValue(filters.additionalOption?.starred, "isStarred"),
		...getValue(filters.additionalOption?.wrongAnswers, "isWrongAnswers"),
		...getValue(filters.additionalOption?.isNote, "isNote"),
		...getValue(isUnanswered, "isUnanswered"),
		...getValue(
			skipCanceledQuestions ? !skipCanceledQuestions : null,
			"isCanceled",
		),
		...getValue(
			isNil(filters.typeOfExam) ? null : filters.typeOfExam === "cem",
			"isCemDb",
		),
		...getValue(cemVersion, "cemVersions"),
		...getValue(
			filters.aiDiscussions?.isAiUnapproved ? true : null,
			"isAiUnapproved",
		),
		...getValue(
			filters.aiDiscussions?.hasUnresolvedReviews ? true : null,
			"hasUnresolvedReviews",
		),
		...(filters.shuffle?.checked
			? { questionsCount: filters.shuffle.questions }
			: {}),
		search: filters.searchValue,
	}).reduce((acc, [key, value]) => {
		if (Array.isArray(value) && value.length === 0) {
			return acc;
		}

		acc[key] = value;

		return acc;
	}, {});
}

const getValue = (value, key) => {
	if (isNil(value)) {
		return {};
	}

	return { [key]: value };
};

export const http = axios.create({
	baseURL: NEW_API_URL,
});

let authorizeRequest = null;

function authorize() {
	if (authorizeRequest !== null) {
		return authorizeRequest;
	}

	authorizeRequest = http.post("/v1/token/refresh").then((res) => {
		authorizeRequest = null;
		return res;
	});

	return authorizeRequest;
}

// axios 1.x: config.headers to AxiosHeaders — ustawiamy na nim, zamiast rozsmarowywać do zwykłego obiektu
http.interceptors.request.use((config) => {
	config.withCredentials = true;
	// FormData: axios/przeglądarka same ustawią multipart z boundary
	if (!(config.data instanceof FormData)) {
		config.headers["Content-Type"] = "application/json";
	}
	config.headers.Service = serviceId;
	if (CF_AUTH_CLIENT_ID && isNativePlatform()) {
		config.headers["CF-Access-Client-Id"] = CF_AUTH_CLIENT_ID;
	}
	if (CF_AUTH_CLIENT_SECRET && isNativePlatform()) {
		config.headers["CF-Access-Client-Secret"] = CF_AUTH_CLIENT_SECRET;
	}
	return config;
});

http.interceptors.response.use(
	(response) => response,
	async (err) => {
		if (err.response?.status === 401) {
			// axios 1.x: config.url zostaje względny (0.18 doklejał baseURL), więc porównujemy końcówkę;
			// 401 na samym odświeżeniu tokenu nie może odpalać kolejnego odświeżenia (pętla)
			if (!err.config.url.endsWith("v1/token/refresh")) {
				return authorize().then(() => http(err.config));
			}
		}
		throw err;
	},
);

class Api {
	getUserSpecialization() {
		if (isPes()) {
			const user = getUserInfo();
			if (user.selectedSpecialisation && !isLek()) {
				return user.selectedSpecialisation.id;
			}
		}

		return null;
	}

	generateDeck(body) {
		ReactGA.event({
			category: "Flashcards",
			action: "User generate deck",
			label: "Generate deck",
		});
		return http
			.post(`/v2${isPes() ? "/pes" : ""}/flashcards/user/deck`, body)
			.then(({ data }) => {
				if (typeof data !== "object") {
					return data;
				}

				return data.id;
			});
	}

	searchDeck(search, queryParams) {
		return http
			.get(`/v2${isPes() ? "/pes" : ""}/flashcards/decks/search`, {
				params: { search, ...queryParams },
			})
			.then(({ data }) => data);
	}

	getDeck(code, embeds) {
		return http
			.get(`/v1/flashcards/user/decks/${code}`, { params: { embeds } })
			.then(({ data }) => data);
	}

	getFlashcardToResolve(deckId) {
		return http
			.get(`/v1/flashcards/user/decks/${deckId}/flashcard`)
			.then(({ data }) => data);
	}

	gradeFlashcard(flashcardRelatedId, grade) {
		return http
			.post(`/v1/flashcards/user/flashcards/${flashcardRelatedId}/grade`, {
				grade,
			})
			.then(({ data }) => data);
	}

	getCountOfFlashcardsInDeckToResolve(deckCode) {
		return http
			.get("/v1/flashcards/user/flashcards/count", { params: { deckCode } })
			.then(({ data }) => data);
	}

	getTags() {
		return http.get("/v1/flashcards/tags").then(({ data }) => data);
	}

	getUserDecks(embeds) {
		return http
			.get("/v1/flashcards/user/decks", { params: { embeds } })
			.then(({ data }) => data);
	}

	duplicateDeck(code) {
		return http
			.post(`/v1/flashcards/user/decks/${code}/duplicate`)
			.then(({ data }) => data);
	}

	removeDecks(codes) {
		return http
			.delete(`/v1/flashcards/user/decks`, { params: { codes } })
			.then(({ data }) => data);
	}

	checkUserAuth() {
		return http.get("/v1/token").then(({ data }) => data);
	}

	removeFlashcardRelation(id) {
		return http
			.delete(`/v1/flashcards/user/flashcardsRelated/${id}`)
			.then(({ status }) => status);
	}

	removeFlashcard(deckId, flashcardId) {
		return http
			.delete(
				`/v2${isPes() ? "/pes" : ""}/flashcards/user/decks/${deckId}/flashcards/${flashcardId}`,
			)
			.then(({ status }) => status);
	}

	updateFlashcard(flashcardId, body) {
		return http
			.patch(`/v1/users/flashcards/${flashcardId}`, body)
			.then(({ data }) => data);
	}

	addFlashcards(deckCode, flashcards) {
		return http
			.post(`/v1/flashcards/user/decks/${deckCode}`, flashcards)
			.then(({ data }) => data);
	}

	getUserStats() {
		return http.get("/v1/flashcards/users/stats").then(({ data }) => data);
	}

	updateDeck(deckCode, dataToUpdate) {
		return http
			.patch(`/v1/flashcards/user/decks/${deckCode}`, dataToUpdate)
			.then(({ data }) => data);
	}

	copyFlashcard(id, deckCode) {
		return http
			.post(`/v1/flashcards/${id}/copy`, { deckCode })
			.then(({ data }) => data);
	}

	generateCourse(body) {
		const { filters } = body;
		const payload = {
			name: body.name,
			filters: {
				...buildQuery(filters),
				questionsPerDay: filters.questionsPerDay,
			},
			...(body.folderIds?.length ? { folderIds: body.folderIds } : {}),
		};
		return http
			.post(`/v2${isPes() ? "/pes" : ""}/courses/user/course`, payload)
			.then(({ data }) => data);
	}

	copyCourse({ courseId }) {
		return http
			.post(`/v2${isPes() ? "/pes" : ""}/courses/user/course`, {
				courseId,
			})
			.then(({ data }) => data);
	}

	getUserCourses() {
		return http.get("/v1/courses/user/courses").then(({ data }) => data);
	}

	getUserCourse(courseId) {
		return http
			.get(`/v3/courses/user/courses/${courseId}`)
			.then(({ data }) => data);
	}

	getUserLesson(courseId, lessonId) {
		return http
			.get(`/v1/courses/user/courses/${courseId}/lessons/${lessonId}`)
			.then(({ data }) => data);
	}

	editCourse(courseId, details) {
		return http
			.patch(`/v1/courses/user/courses/${courseId}`, details)
			.then(({ data }) => data);
	}

	removeCourses(coursesIds) {
		return http
			.delete(`/v1/courses/user/courses`, {
				params: {
					ids: coursesIds,
				},
			})
			.then(({ data }) => data);
	}

	getRelatedDepartmentsToArticles() {
		return http
			.get("/v1/articles/relations/departments")
			.then(({ data }) => data);
	}

	getArticle(id, query) {
		return http
			.get(`/v1/articles/${id}/published`, { params: query })
			.then(({ data }) => data);
	}

	getLinkRelation(hash) {
		return http
			.get(`/v1/articles/link/relation/${hash}`)
			.then(({ data }) => data);
	}

	getArticlesByQuestionIds(questionsIds) {
		return http
			.get("/v1/articles/search", { params: { questionsIds } })
			.then(({ data }) => data)
			.catch(() => []);
	}

	getUserNotifications(params) {
		const specId = this.getUserSpecialization();
		return http
			.get("/v1/user/notifications", {
				params: {
					...params,
					specId,
				},
			})
			.then(({ data }) => data)
			.catch((_err) => {
				return [];
			});
	}

	removeNotification(id) {
		return http.delete(`/v1/notification/${id}`).then(() => true);
	}

	getNotificationsCount() {
		const specId = this.getUserSpecialization();
		return http
			.get("/v1/user/notifications/count", { params: { specId } })
			.then(({ data }) => data.count);
	}

	removeNotificationSubscription(itemId, type) {
		const specId = this.getUserSpecialization();
		return http
			.delete(`/v1/notifications/subscriptions/items/${itemId}/${type}`, {
				params: { specId },
			})
			.then(() => null);
	}

	removeNotificationHard(id) {
		const specId = this.getUserSpecialization();
		return http
			.delete(`/v1/notification/${id}/hard`, { params: { specId } })
			.then(() => true);
	}

	getCountOfUnresolvedComments() {
		return http
			.get("/v1/pes/notifications/count")
			.then(({ data }) => data)
			.catch(() => []);
	}

	getCoursesTemplates(filters = {}) {
		return http
			.get(`/v2${isPes() ? "/pes" : ""}/courses/user/templates`, {
				params: {
					...filters,
				},
			})
			.then(({ data }) => data)
			.catch(() => []);
	}

	generateCourseFromTemplate(templateId, folderIds) {
		return http
			.post(
				`/v2${isPes() ? "/pes" : ""}/courses/user/templates/${templateId}/course`,
				{
					...(folderIds?.length ? { folderIds } : {}),
				},
			)
			.then(({ data }) => data);
	}

	notificationGlobalAction(action) {
		const specId = this.getUserSpecialization();
		return http.delete(`/v1/notifications/${action}`, { params: { specId } });
	}

	getExam(id) {
		return http
			.get(`/v2${isPes() ? "/pes" : ""}/user/exams/${id}`)
			.then(({ data }) => data);
	}

	getDiscussion(id) {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/questions/${id}/discussion`)
			.then(({ data }) => data);
	}

	generateExam(filters, name, typeDetails = {}, folderIds) {
		const payload = {
			...buildQuery(filters),
			...typeDetails,
			name,
			...(folderIds?.length ? { folderIds } : {}),
		};
		return http
			.post(`/v2${isPes() ? "/pes" : ""}/user/exam`, payload)
			.then(({ data }) => data);
	}

	getCount(filters) {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/questions/count`, {
				params: buildQuery(filters),
			})
			.then(({ data }) => data);
	}

	getTerms() {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/terms`)
			.then(({ data }) => data);
	}

	getQuestionDetails(id) {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/questions/${id}/details`)
			.then(({ data }) => data);
	}

	getExamQuestionDetails(id, examId) {
		return http
			.get(
				`/v1${isPes() ? "/pes" : ""}/questions/${id}/details?examId=${examId}`,
			)
			.then(({ data }) => data);
	}

	getSpecialisations() {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/specialisations`)
			.then(({ data }) => data);
	}

	getUnfinishedExams({ search, ...filters }) {
		return http
			.get(`/v2${isPes() ? "/pes" : ""}/user/exams`, {
				params: {
					...filters,
					name: search,
					status: "unfinished",
				},
			})
			.then(({ data }) => data);
	}

	getFinishedExams({ search, ...filters }) {
		return http
			.get(`/v2${isPes() ? "/pes" : ""}/user/exams`, {
				params: {
					...filters,
					name: search,
					status: "finished",
				},
			})
			.then(({ data }) => data);
	}

	getAllUserExams({ search, ...filters }) {
		return http
			.get(`/v2${isPes() ? "/pes" : ""}/user/exams`, {
				params: {
					...filters,
					name: search,
				},
			})
			.then(({ data }) => data);
	}

	getCemVersions() {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/cemVersions`)
			.then(({ data }) => data);
	}

	addComment(id, { comment, parentId, receiverId, image }) {
		return http
			.post(`/v1${isPes() ? "/pes" : ""}/questions/${id}/comment`, {
				comment,
				parentId,
				parentUserId: receiverId,
				...(image ? { image } : {}),
			})
			.then(({ data }) => data);
	}

	addForumComment(_id, { comment, parentId, receiverId, image }) {
		return http
			.post(`/v1${isPes() ? "/pes" : ""}/user/forum/comment`, {
				comment,
				parentId,
				parentUserId: receiverId,
				...(image ? { image } : {}),
			})
			.then(({ data }) => data);
	}

	getQuestionComments(id, options) {
		return http
			.get(`/v2${isPes() ? "/pes" : ""}/questions/${id}/comments`, {
				params: {
					sort: options.sort,
					type: options.type,
				},
			})
			.then(({ data }) => data);
	}

	getComments({ page, take }) {
		return http
			.get(`/v2${isPes() ? "/pes" : ""}/comments`, {
				params: {
					page,
					take,
				},
			})
			.then(({ data }) => data);
	}
	getForumCommentSubcomments(parentId) {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/user/comments/${parentId}/forum`)
			.then(({ data }) => data);
	}

	getForumComments({ page, take, search }) {
		return http
			.get(`/v2${isPes() ? "/pes" : ""}/comments`, {
				params: {
					page,
					take,
					commentType: "FORUM",
					parentId: "null",
					search,
				},
			})
			.then(({ data }) => data);
	}

	getComment(id) {
		return http
			.get(`/v2${isPes() ? "/pes" : ""}/comments/${id}`)
			.then(({ data }) => data);
	}

	getMe() {
		return http.get("/v1/me").then(({ data }) => data);
	}

	deleteComment(id) {
		return http
			.delete(`/v1${isPes() ? "/pes" : ""}/comments/${id}`)
			.then(({ data }) => data);
	}

	removeUnfinishedExams(ids) {
		return http
			.delete(`/v1${isPes() ? "/pes" : ""}/exams/unfinished`, {
				params: {
					ids,
				},
			})
			.then(({ data }) => data);
	}

	removeFinishedExams(ids) {
		return http
			.delete(`/v1${isPes() ? "/pes" : ""}/exams/finished`, {
				params: {
					ids,
				},
			})
			.then(({ data }) => data);
	}

	deleteExams(ids) {
		return http
			.delete(`/v1${isPes() ? "/pes" : ""}/user/exams`, {
				params: {
					ids,
				},
			})
			.then(({ data }) => data);
	}

	updateExam(id, { name, status, state, type }) {
		return http
			.patch(`/v2${isPes() ? "/pes" : ""}/exams/${id}`, {
				name,
				status,
				state,
				type,
			})
			.then(({ data }) => {
				return data;
			})
			.catch((err) => err);
	}

	getCooperations() {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/cooperations/`)
			.then(({ data }) => data)
			.catch(() => null);
	}

	closeCooperation(id) {
		return http.patch(`/v1/cooperations/${id}/`, {
			active: false,
		});
	}

	getUniversities() {
		return http.get("/v1/universities").then(({ data }) => data);
	}

	setStorageItem(key, value) {
		return http.post(`/v1${isPes() ? "/pes" : ""}/user-store/${key}`, value);
	}

	getStorageItem(key) {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/user-store/${key}`)
			.then(({ data }) => data);
	}

	deleteStorageItem(key) {
		return http.delete(`/v1${isPes() ? "/pes" : ""}/user-store/${key}`);
	}

	getCategories() {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/categories`)
			.then(({ data }) => data);
	}

	getAppConfig() {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/app/config`)
			.then(({ data }) => data)
			.catch(() => defaultAppConfig);
	}

	toggleStarred(id) {
		return http
			.patch(`/v1${isPes() ? "/pes" : ""}/questions/${id}/starred`)
			.then(({ data }) => data);
	}

	getStatistics(query) {
		return http
			.get(`/v2${isPes() ? "/pes" : ""}/statistics`, { params: query })
			.then(({ data }) => data)
			.catch(() => null);
	}

	getQuestionsStatistics(id) {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/questions/${id}/statistics`)
			.then(({ data }) => data);
	}

	setAnswer(id, questionId, answer) {
		return http
			.post(
				`/v1${isPes() ? "/pes" : ""}/exams/${id}/questions/${questionId}/answer`,
				{
					answer,
				},
			)
			.then(({ data }) => data);
	}

	setExcludedAnswer(id, questionId, answer) {
		return http
			.post(
				`/v1${isPes() ? "/pes" : ""}/exams/${id}/questions/${questionId}/answer/excluded`,
				{
					answer,
				},
			)
			.then(({ data }) => data);
	}

	deleteAccount() {
		return http.delete("/v1/me/");
	}

	getNote(id) {
		return http
			.get(`/v1/${isPes() ? "/pes" : ""}/questions/${id}/notes`)
			.then(({ data }) => data);
	}

	addNote(id, note) {
		return http
			.post(`/v1/${isPes() ? "/pes" : ""}/questions/${id}/notes`, {
				note,
			})
			.then(({ data }) => data);
	}

	searchFolders(params) {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/folders/search`, { params })
			.then(({ data }) => data);
	}

	createFolder(name, details) {
		return http
			.post(`/v1${isPes() ? "/pes" : ""}/folders`, { name, details })
			.then(({ data }) => data);
	}

	updateFolder(id, name, details) {
		return http
			.patch(`/v1${isPes() ? "/pes" : ""}/folders/${id}`, { name, details })
			.then(({ data }) => data);
	}

	getFolder(id, params) {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/folders/${id}`, { params })
			.then(({ data }) => data);
	}

	deleteFolder(id) {
		return http
			.delete(`/v1${isPes() ? "/pes" : ""}/folders/${id}`)
			.then(({ data }) => data);
	}

	getFolderItemsOffset(id, params) {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/folders/${id}/items`, { params })
			.then(({ data }) => data);
	}

	addFolderItems(id, items) {
		return http
			.post(`/v1${isPes() ? "/pes" : ""}/folders/${id}/items`, { items })
			.then(({ data }) => data);
	}

	removeFolderItems(id, items) {
		return http
			.post(`/v1${isPes() ? "/pes" : ""}/folders/${id}/items/delete`, {
				items,
			})
			.then(({ data }) => data);
	}

	moveFolderItems(folderId, body) {
		return http
			.patch(`/v1${isPes() ? "/pes" : ""}/folders/${folderId}/items/move`, body)
			.then(({ data }) => data);
	}

	activateUserPromocode(code) {
		return http
			.post(`/v1/${isPes() ? "/pes" : ""}user/promocodes/${code}/activate`)
			.then(({ data }) => data);
	}

	validatePromocode(code) {
		return http
			.get(`/v1/${isPes() ? "/pes" : ""}/promocodes/${code}/validate`)
			.then(() => true)
			.catch(() => false);
	}

	reportQuestion(id, data) {
		return http
			.post(`/v1${isPes() ? "/pes" : ""}/questions/${id}/report`, data)
			.then(({ data }) => data);
	}

	reportArticle(id, data) {
		return http
			.post(`/v1/articles/${id}/report`, data)
			.then(({ data }) => data);
	}

	updatePassword(oldPassword, newPassword) {
		return http
			.put("/v1/me/password", {
				oldPassword,
				newPassword,
			})
			.then(({ data }) => data);
	}

	getArticlesCategories() {
		return http.get("/v1/articles/categories").then(({ data }) => data);
	}

	updateMe({
		specialization,
		examType,
		university,
		email,
		serviceId,
		avatarUrl,
	}) {
		return http.patch("/v1/me/details", {
			...(university ? { universityId: university.id } : {}),
			...(specialization ? { specialisationId: specialization } : {}),
			...(examType ? { examTypeId: examType.id } : {}),
			...(email ? { email } : {}),
			...(avatarUrl || avatarUrl === "" ? { avatarUrl } : {}),
			...(serviceId ? { serviceId } : {}),
		});
	}

	resetPassword(email) {
		return http.post("/v1/reset/password", { email }).then(({ data }) => data);
	}

	validateToken(token) {
		return http.get(`/v1/password/tokens/${token}`);
	}

	signUpUser({ email, username, password }) {
		return http.post("/v1/user/register", {
			email,
			username,
			password,
		});
	}

	setNewPassword(token, password) {
		return http.post("/v1/reset/password/new", {
			token,
			password,
		});
	}

	logout() {
		return http.delete("/v1/token");
	}

	login(data, isPes) {
		return http
			.post(`/v1/${isPes ? "/pes" : ""}/token`, data)
			.then(({ data }) => data);
	}

	getConsultants() {
		return http.get("/v1/pes/consultants").then(({ data }) => data);
	}

	toggleCommentLike(id) {
		return http
			.post(`/v2${isPes() ? "/pes" : ""}/comments/${id}/like/toggle`)
			.then(({ data }) => data);
	}

	async uploadImage(file) {
		const formData = new FormData();
		formData.append("file", file);

		try {
			const response = await http.post("/v2/users/upload/image", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			return response.data;
		} catch {
			/*  nothing */
		}
	}

	getSources() {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/sources`)
			.then(({ data }) => data);
	}

	generateAiQuestionDetails(questionId) {
		return http
			.post(`/v1${isPes() ? "/pes" : ""}/ai/content/answersDetails`, {
				questionId,
			})
			.then(({ data, status }) => ({ data, status }))
			.catch(() => null);
	}

	generateAiChatText(body) {
		return http
			.post(`/v1${isPes() ? "/pes" : ""}/ai/content/questionTutor`, body)
			.then(({ data, status }) => ({ data, status }))
			.catch(() => null);
	}

	getAiChatResponses({ promptType, itemType, itemId }) {
		return http
			.get(
				`/v1${isPes() ? "/pes" : ""}/ai/users/prompts/${promptType}/${itemType}/${itemId}`,
			)
			.then(({ data }) => data);
	}

	getAiResponseRate(id) {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/ai/responses/${id}/rate`)
			.then(({ data }) => data.like)
			.catch(() => null);
	}

	getAiResponseStreamUrl(type) {
		return `${NEW_API_URL}v1${isPes() ? "/pes" : ""}/ai/content/${type}/stream`;
	}

	getAiResponseRegenerateStreamUrl(type) {
		return `${NEW_API_URL}v1${isPes() ? "/pes" : ""}/ai/content/${type}/regenerate/stream`;
	}

	getExamStats(id) {
		return http
			.get(`/v2${isPes() ? "/pes" : ""}/user/exams/${id}/statistics`)
			.then(({ data }) => data);
	}

	getExamsStats(examsIds) {
		return http
			.get(`/v2${isPes() ? "/pes" : ""}/user/statistics/exams`, {
				params: {
					examsIds,
				},
			})
			.then(({ data }) => data);
	}

	toggleAiResponseRate(id, like) {
		return http
			.post(`/v1${isPes() ? "/pes" : ""}/ai/responses/${id}/rate`, { like })
			.then(({ data }) => data)
			.catch(() => null);
	}

	toggleAiResponseApproval(id) {
		return http
			.post(`/v1${isPes() ? "/pes" : ""}/ai/responses/${id}/approve/toggle`)
			.then(({ data }) => data);
	}

	updateAiResponseContent(id, response) {
		return http
			.patch(`/v1${isPes() ? "/pes" : ""}/ai/responses/${id}`, { response })
			.then(({ data }) => data);
	}

	getAiResponseContext(id, params = {}) {
		const query = {};
		if (params.filter) query.filter = params.filter;
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/ai/responses/${id}/context`, {
				params: query,
			})
			.then(({ data }) => data);
	}

	createAiResponseReview(id, review) {
		return http
			.post(`/v1${isPes() ? "/pes" : ""}/ai/responses/${id}/reviews`, {
				review,
			})
			.then(({ data }) => data);
	}

	updateAiReviewStatus(reviewId, status) {
		return http
			.post(`/v1${isPes() ? "/pes" : ""}/ai/reviews/${reviewId}/status`, {
				status,
			})
			.then(({ data }) => data);
	}

	getAiResponseReviews(id, params = {}) {
		const query = {};
		if (params.status) query.status = params.status;
		if (params.sortBy) query.sortBy = params.sortBy;
		if (params.sortOrder) query.sortOrder = params.sortOrder;
		if (params.limit != null) query.limit = params.limit;
		if (params.offset != null) query.offset = params.offset;
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/ai/responses/${id}/reviews`, {
				params: query,
			})
			.then(({ data }) => data);
	}

	sendSlackNotification(type, body) {
		return http
			.post(`/v1${isPes() ? "/pes" : ""}/slack/${type}/notification`, body)
			.then(({ data }) => data)
			.catch(() => null);
	}

	getExamPreviewDetails(filters) {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/user/exam/preview/details`, {
				params: {
					...buildQuery(filters),
					examType: filters?.typeDetails?.type,
					timePerQuestion: filters?.typeDetails?.timePerQuestion,
					shuffleCount: filters.shuffleCount,
				},
			})
			.then(({ data }) => data);
	}

	resetSection(sectionId, courseId) {
		return http
			.patch(`/v1/courses/user/courses/${courseId}/sections/${sectionId}/reset`)
			.then(({ data }) => data)
			.catch(() => null);
	}

	patchLesson(lessonId, courseId, data) {
		return http
			.patch(`/v1/courses/user/courses/${courseId}/lessons/${lessonId}`, data)
			.then(({ data }) => data);
	}

	getForumCommentsCount() {
		return http
			.get(`/v2${isPes() ? "/pes" : ""}/comments/forum/count`)
			.then(({ data }) => data)
			.catch(() => null);
	}

	getUserNote(noteId) {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/users/user-notes/${noteId}`)
			.then(({ data }) => data);
	}

	deleteUserNote(noteId) {
		return http
			.delete(`/v1${isPes() ? "/pes" : ""}/users/user-notes/${noteId}`)
			.then(({ data }) => data);
	}

	updateUserNote(noteId, { note, name }) {
		return http
			.patch(`/v1${isPes() ? "/pes" : ""}/users/user-notes/${noteId}`, {
				note,
				name,
			})
			.then(({ data }) => data);
	}

	createUserNote({ note, name, folderId }) {
		return http
			.post(`/v1${isPes() ? "/pes" : ""}/users/user-notes`, {
				note,
				name,
				folderId,
			})
			.then(({ data }) => data);
	}

	searchUserNotes(params) {
		return http
			.get(`/v1${isPes() ? "/pes" : ""}/users/user-notes/search`, { params })
			.then(({ data }) => data);
	}

	reportCourseReservation(body) {
		return http
			.post(`/v1/courses/user/templates/reports`, body)
			.then(({ data }) => data)
			.catch(() => null);
	}
}

const api = new Api();

export default api;
